import { useState } from "react";
import BoltButton from "../../../../Components/UI/Buttons/BoltButton";
import SwitchButton from "../../../../Components/SwitchButton/SwitchButton";
import ManageServices from "../../UserRegistration/Components/Shippers/Components/ManageServices/ManageServices";
import AllowedCarriers from "./AllowedCarriers";
import { useDispatch } from "react-redux";
import { showSnackBar } from "../../../../store/common/snackBarSlice";
import {
  updateMainUserAccountCharge,
  updateCustomCarrierStatus,
} from "../../../../services/AdminServices/Companies/companies.services";
import { useTranslation } from "react-i18next";
import i18n from "../../../../i18n";

const Carriers = ({ shipperData, updateShipper }) => {
  const { t } = useTranslation("admin");
  const dispatch = useDispatch();
  const [activeSubTab, setActiveSubTab] = useState(0);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [servicesModal, setServicesModal] = useState();
  const [editingMarkupId, setEditingMarkupId] = useState(null);
  const [markupValue, setMarkupValue] = useState("");
  const [updatingMarkup, setUpdatingMarkup] = useState(false);

  const UpdateCompanyStatus = async (check, id) => {
    setStatusUpdating(true);

    try {
      // Call API
      const updated = await updateCustomCarrierStatus(
        { is_enable: `${check}` },
        id
      );

      // Handle API errors gracefully
      if (updated?.error) {
        dispatch(
          showSnackBar({ text: updated.error.message, severity: "error" })
        );
        return;
      }

      const newIsEnable = updated?.data?.result?.is_enable;
      if (!newIsEnable) {
        dispatch(
          showSnackBar({
            text: t("controls.unexpectedResponseFromServer"),
            severity: "warning",
          })
        );
        return;
      }

      const updatedCompanies = shipperData?.user_shipping_companies?.map(
        (company) =>
          company.id === id ? { ...company, is_enable: newIsEnable } : company
      );

      // ✅ Update parent user state
      updateShipper(
        shipperData.id,
        "user_shipping_companies",
        updatedCompanies
      );

      // ✅ Show success message
      dispatch(
        showSnackBar({
          text: check
            ? t("controls.statusChangedActive")
            : t("controls.statusChangedInactive"),
          severity: check ? "success" : "info",
        })
      );
    } catch (error) {
      console.error("UpdateCompanyStatus error:", error);
      dispatch(
        showSnackBar({
          text: t("controls.somethingWentWrongWhileUpdating"),
          severity: "error",
        })
      );
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleMarkupEdit = (record) => {
    setEditingMarkupId(record.id);
    setMarkupValue(record?.service_charge || "");
  };

  const handleMarkupCancel = () => {
    setEditingMarkupId(null);
    setMarkupValue("");
  };

  const handleMarkupSave = async (record) => {
    if (editingMarkupId !== record.id) return;

    setUpdatingMarkup(true);
    const res = await updateMainUserAccountCharge(
      { service_charge: markupValue },
      record.id
    );
    setUpdatingMarkup(false);

    if (res.error) {
      const message =
        res?.error?.response?.data?.message ||
        res?.error?.message ||
        i18n.t("admin:controls.failedToUpdateServiceCharge");
      dispatch(
        showSnackBar({
          text: message,
          severity: "error",
        })
      );
    } else {
      const updatedCompanies = shipperData?.user_shipping_companies?.map(
        (company) =>
          company.id === record.id
            ? { ...company, service_charge: markupValue }
            : company
      );

      // ✅ Update parent user state
      updateShipper(
        shipperData.id,
        "user_shipping_companies",
        updatedCompanies
      );

      dispatch(
        showSnackBar({
          text: t("controls.serviceChargeUpdatedSuccessfully"),
          severity: "success",
        })
      );
    }
    setEditingMarkupId(null);
    setMarkupValue("");
  };

  const renderCarrierCompaniesStatus = () => {
    return (
      <div className="bg-ui-surface rounded-lg border border-ui-border p-3">
        <div className="space-y-1">
          {shipperData?.user_shipping_companies?.map((company, index) => (
            <div
              key={index}
              className="flex items-center justify-between py-2 px-3 bg-ui-surface-sunken hover:bg-ui-surface-sunken rounded-md transition-colors"
            >
              <div className="flex-1 min-w-0">
                <span className="font-medium text-sm text-ui-text truncate">
                  {company?.name?.toUpperCase()}
                </span>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-ui-text-muted">
                    {t("controls.markupLabel")}
                  </span>
                  {editingMarkupId === company?.id ? (
                    <div className="flex items-center gap-2 bg-ui-surface border border-brand rounded-lg px-2 py-1 shadow-sm">
                      <input
                        type="number"
                        value={markupValue}
                        onChange={(e) => setMarkupValue(e.target.value)}
                        className="w-20 h-7 px-2 text-sm border-0 focus:outline-none focus:ring-0 bg-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        placeholder="0.00"
                        step="0.01"
                        min="0"
                        autoFocus
                      />
                      <span className="text-xs text-ui-text-muted">%</span>
                      <div className="flex items-center gap-1 ml-1">
                        <button
                          onClick={() => handleMarkupSave(company)}
                          disabled={updatingMarkup}
                          className="p-1.5 text-state-success hover:text-state-success hover:bg-state-success-bg rounded disabled:opacity-50 transition-colors"
                          title={t("controls.saveMarkup")}
                        >
                          {updatingMarkup ? (
                            <svg
                              className="w-4 h-4 animate-spin"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              />
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              />
                            </svg>
                          ) : (
                            <svg
                              className="w-4 h-4"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          )}
                        </button>
                        <button
                          onClick={handleMarkupCancel}
                          className="p-1.5 text-state-danger hover:text-state-danger hover:bg-state-danger-bg rounded transition-colors"
                          title={t("controls.cancelEditing")}
                        >
                          <svg
                            className="w-4 h-4"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div
                      className="flex items-center gap-2 bg-ui-surface-sunken hover:bg-ui-border rounded-lg px-3 py-1.5 cursor-pointer transition-colors group"
                      onClick={() => handleMarkupEdit(company)}
                    >
                      <span className="text-sm font-medium text-ui-text">
                        {company?.service_charge > 0
                          ? company?.service_charge
                          : t("controls.default")}{" "}
                        %
                      </span>
                      <svg
                        className="w-3.5 h-3.5 text-ui-text-subtle group-hover:text-ui-text-muted transition-colors"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                      </svg>
                      <span className="text-xs text-ui-text-muted group-hover:text-ui-text-muted transition-colors">
                        {t("controls.clickToEdit")}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center space-x-3 ml-4">
                <div
                  className={`${
                    statusUpdating ? "opacity-50 pointer-events-none" : ""
                  }`}
                >
                  <SwitchButton
                    onChange={(e, value) =>
                      UpdateCompanyStatus(value, company.id)
                    }
                    checked={[true, "true", "1"].includes(
                      company?.is_enable?.admin
                    )}
                  />
                </div>
                {/* <button
                  onClick={() =>
                    setServicesModal(
                      shipperData?.user_shipping_companies?.find(
                        (comp) => toCamelCase(comp?.name) === company?.name
                      )
                    )
                  }
                  className="p-1.5 text-brand hover:text-brand-hover hover:bg-brand-light rounded transition-colors"
                  title="Manage Services"
                >
                  <AiOutlineEdit className="w-4 h-4" />
                </button> */}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <ManageServices
        open={!!servicesModal}
        carrier={servicesModal}
        setOpen={setServicesModal}
        Android12Switch={SwitchButton}
      />

      <div className="flex justify-start">
        <div className="flex">
          <BoltButton
            label={t("controls.defaultCarriers")}
            variant={activeSubTab === 0 ? "primary" : "outline"}
            size="md"
            onClick={() => setActiveSubTab(0)}
            className={`px-6 py-2 rounded-lg transition-all duration-200 ${
              activeSubTab === 0
                ? "bg-brand text-white shadow-md"
                : "bg-ui-border text-ui-text-muted hover:bg-ui-border"
            }`}
          />
          <BoltButton
            label={t("controls.allowedAdditionalCarriers")}
            variant={activeSubTab === 1 ? "primary" : "outline"}
            size="md"
            onClick={() => setActiveSubTab(1)}
            className={`px-6 py-2 rounded-lg transition-all duration-200 ml-3 ${
              activeSubTab === 1
                ? "bg-brand text-white shadow-md"
                : "bg-ui-border text-ui-text-muted hover:bg-ui-border"
            }`}
          />
        </div>
      </div>

      {activeSubTab === 0 ? (
        renderCarrierCompaniesStatus()
      ) : (
        <AllowedCarriers shipperData={shipperData} />
      )}
    </div>
  );
};

export default Carriers;
